import { IonIcon } from "@ionic/react";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { chevronDownOutline, closeOutline, searchOutline } from "ionicons/icons";
import { SearchField } from "@/components/atoms/search-field";
import { ModalSheetAutoHeight } from "@/components/modal/sheet/auto-height";
import { type DiscoverFacet, type DiscoverFilters, activeFilterCount } from "@/lib/discover-facets";

type DiscoverFilterBarProps = {
  filters: DiscoverFilters;
  onFiltersChange: (filters: DiscoverFilters) => void;
  functions: DiscoverFacet[];
  cities: DiscoverFacet[];
  search: string;
  onSearchChange: (value: string) => void;
};

/**
 * Two of the three questions, at the top of the screen.
 *
 * _When_ used to live here as four period chips and is now the calendar above this bar — a period
 * is a window, and a window is something you page through rather than pick from a list somebody
 * wrote in advance.
 *
 * Function and city stay here. They are counted from what the visible month brought back, so they
 * only appear once there is something to choose between — a "Plaats" button on a list that is all
 * one city is a button that can only disappoint.
 *
 * Zoeken hoort er sinds kort bij. Dat stond als vergrootglas in de kop, naast de titel en de
 * organisatie, en dat is de plek voor wie je bent -- niet voor wat je zoekt. Alle drie de vragen
 * die de lijst inperken staan nu op dezelfde regel.
 */
export function DiscoverFilterBar({
  filters,
  onFiltersChange,
  functions,
  cities,
  search,
  onSearchChange,
}: DiscoverFilterBarProps) {
  const { t } = useTranslation();
  const functionSheet = useRef<HTMLIonModalElement>(null);
  const citySheet = useRef<HTMLIonModalElement>(null);
  // Openklappen en niet altijd open: het veld is breder dan de twee chips samen, en een lijst die
  // je in negen van de tien keer gewoon leest hoort niet met een leeg invoervak te beginnen. Een
  // ingevulde zoekterm houdt hem open -- anders verdween wat je zocht uit beeld.
  const [searchOpen, setSearchOpen] = useState(false);

  const active = activeFilterCount(filters);

  if (searchOpen || search) {
    return (
      <div className="flex flex-col gap-2 pb-1">
        <SearchField
          value={search}
          onChange={onSearchChange}
          onBlur={() => {
            if (!search) {
              setSearchOpen(false);
            }
          }}
          placeholder={t("common:search.placeholder")}
          autoFocus={searchOpen}
          debounce={300}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2 pb-1">
      <div className="flex flex-wrap items-center gap-2">
        {functions.length > 1 && (
          <FilterChip
            label={filters.userFunction ?? t("common:discover.filter.function")}
            isActive={!!filters.userFunction}
            hasChevron
            onClick={() => functionSheet.current?.present()}
          />
        )}

        {cities.length > 1 && (
          <FilterChip
            label={filters.city ?? t("common:discover.filter.city")}
            isActive={!!filters.city}
            hasChevron
            onClick={() => citySheet.current?.present()}
          />
        )}

        {active > 0 && (
          <button
            type="button"
            onClick={() => onFiltersChange({ userFunction: null, city: null })}
            className="touch-target inline-flex items-center gap-1 rounded-full px-2 py-1 text-sm font-medium text-txt-secondary transition-opacity active:opacity-70"
          >
            <IonIcon icon={closeOutline} className="size-3.5" aria-hidden="true" />
            {t("common:discover.filter.clear")}
          </button>
        )}

        {/* Rechts op de regel, ook als er geen chips zijn: zoeken hangt niet af van wat de maand
            toevallig opleverde, en het is de enige van de drie die er altijd staat. */}
        <button
          type="button"
          onClick={() => setSearchOpen(true)}
          aria-label={t("common:search.label")}
          className="touch-target ml-auto rounded-lg p-2 transition-opacity active:opacity-70"
        >
          <IonIcon icon={searchOutline} className="text-lg text-txt-primary" />
        </button>
      </div>

      <FacetSheet
        modalRef={functionSheet}
        title={t("common:discover.filter.function")}
        facets={functions}
        selected={filters.userFunction}
        onSelect={(value) => onFiltersChange({ ...filters, userFunction: value })}
      />
      <FacetSheet
        modalRef={citySheet}
        title={t("common:discover.filter.city")}
        facets={cities}
        selected={filters.city}
        onSelect={(value) => onFiltersChange({ ...filters, city: value })}
      />
    </div>
  );
}

function FilterChip({
  label,
  isActive,
  hasChevron,
  onClick,
}: {
  label: string;
  isActive: boolean;
  hasChevron?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1 rounded-full border border-solid px-3.5 py-1.5 text-sm font-medium transition-opacity active:opacity-70 ${
        isActive
          ? "border-txt-primary bg-txt-primary text-txt-inverted"
          : "border-bordercolor-primary text-txt-secondary"
      }`}
    >
      {label}
      {hasChevron && <IonIcon icon={chevronDownOutline} className="size-3.5" aria-hidden="true" />}
    </button>
  );
}

/**
 * One choice, with counts.
 *
 * The counts are what make this worth a sheet rather than a dropdown: "Bar — 12" tells you whether
 * the filter is worth setting before you set it, and a value with nothing behind it never appears
 * at all.
 */
function FacetSheet({
  modalRef,
  title,
  facets,
  selected,
  onSelect,
}: {
  modalRef: React.RefObject<HTMLIonModalElement>;
  title: string;
  facets: DiscoverFacet[];
  selected: string | null;
  onSelect: (value: string | null) => void;
}) {
  const { t } = useTranslation();

  const choose = (value: string | null) => {
    onSelect(value);
    modalRef.current?.dismiss();
  };

  return (
    <ModalSheetAutoHeight ref={modalRef}>
      <div className="flex flex-col gap-3 px-6 pb-6 pt-4">
        <h2 className="text-lg font-semibold leading-[1.4] text-txt-primary">{title}</h2>

        <div className="flex flex-col">
          <FacetRow
            label={t("common:discover.filter.all")}
            isSelected={selected === null}
            onClick={() => choose(null)}
          />
          {facets.map((facet) => (
            <FacetRow
              key={facet.value}
              label={facet.value}
              count={facet.count}
              isSelected={selected === facet.value}
              onClick={() => choose(facet.value)}
            />
          ))}
        </div>
      </div>
    </ModalSheetAutoHeight>
  );
}

function FacetRow({
  label,
  count,
  isSelected,
  onClick,
}: {
  label: string;
  count?: number;
  isSelected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center justify-between gap-3 border-b border-solid border-bordercolor-primary py-3 text-left transition-opacity active:opacity-70"
    >
      <span
        className={`text-md leading-[1.5] ${
          isSelected ? "font-semibold text-txt-primary" : "font-medium text-txt-secondary"
        }`}
      >
        {label}
      </span>
      {count !== undefined && (
        <span className="text-sm tabular-nums text-txt-tertiary">{count}</span>
      )}
    </button>
  );
}
